import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface PaginationFooterProps {
  readonly rangeStart: number;
  readonly rangeEnd: number;
  readonly hasPrevious: boolean;
  readonly hasNext: boolean;
  readonly onPrevious: () => void;
  readonly onNext: () => void;
  readonly isLoading: boolean;
}

/**
 * Shows a range, not "1–20 of 248".
 *
 * The API derives pages by fetching `limit + 1` rows rather than issuing a
 * COUNT, so no total exists to display. Inventing one would mean a second
 * full-table query on every page load.
 */
export function PaginationFooter({
  rangeStart,
  rangeEnd,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  isLoading,
}: PaginationFooterProps) {
  return (
    <div className="flex items-center justify-between border-t border-border px-4 py-3">
      <span className="text-[13px] text-muted-foreground">
        {isLoading
          ? 'loading…'
          : rangeEnd === 0
            ? '0 results'
            : `showing ${String(rangeStart)}–${String(rangeEnd)}`}
      </span>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPrevious} disabled={!hasPrevious || isLoading}>
          <ChevronLeftIcon aria-hidden="true" />
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={onNext} disabled={!hasNext || isLoading}>
          Next
          <ChevronRightIcon aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}
